import { LoadingSpinner } from "@/components/ui/LoadingSpinner/LoadingSpinner";
import { SelectDestinationFolderSlideover } from "@/components/features/matter/file-repo/signature-requests/SelectDestinationFolderSlideover";
import { Button } from "@/components/ui/shadcn/Button/Button";
import { TooltipWrapper } from "@/components/ui/shadcn/Tooltip/TooltipWrapper";
import { Typography } from "@/components/ui/Typography/Typography";
import { downloadMessageMediaFile } from "@/components/ui/CommunicationSlideover/downloadMessageMediaFile";
import { CommunicationVideo } from "@/components/ui/CommunicationSlideover/CommunicationVideo";
import { CommunicationImagePreview } from "@/components/ui/CommunicationSlideover/CommunicationImagePreview";
import { retrieveMatterQueryOptions } from "@/lib/data/react-query/matters/matter";
import { smsMediaPreviewQueryOptions } from "@/lib/data/react-query/contacts/contact-sms";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import { cn } from "@/lib/utils/cn";
import { formatLocalOrIntlPhoneNumber } from "@/lib/utils/format";
import { parseVCFFromUrl, type VCardContact } from "@/lib/utils/vcfParser";
import { useQuery } from "@tanstack/react-query";
import {
  FileCheck,
  FilePlus,
  IdCard,
  Mail,
  MapPin,
  Phone,
  ZoomIn,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";

export const CommunicationAttachment = ({
  s3Key,
  matterId,
  direction,
}: {
  s3Key: string;
  matterId?: string;
  direction: "INBOUND" | "OUTBOUND";
}) => {
  const preview = useQuery(smsMediaPreviewQueryOptions(s3Key));
  const fileFromPresignedURLData = preview.data;
  const { data: matter } = useQuery(retrieveMatterQueryOptions(matterId ?? ""));

  const { openFileModal } = useGlobalModal();
  const [isAdded, setIsAdded] = useState(
    matter?.data.files.some((file) => file.name === s3Key),
  );
  const [isPreparingFile, setIsPreparingFile] = useState(false);
  const [isFolderSelectOpen, setIsFolderSelectOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const previewTriggerRef = useRef<HTMLButtonElement>(null);

  // Check if this is a vCard file (business card)
  const isVCardFile =
    s3Key.includes(".vcf") ||
    s3Key.includes("vcard") ||
    s3Key.includes("text/vcard") ||
    s3Key.includes("text/x-vcard");

  // VCF parsing state
  const [contact, setContact] = useState<VCardContact | null>(null);
  const [isLoadingVCF, setIsLoadingVCF] = useState(false);
  const [vcfError, setVcfError] = useState<string | null>(null);

  // Parse VCF file when it's a vCard file
  useEffect(() => {
    const parseVCF = async () => {
      if (!isVCardFile || !fileFromPresignedURLData?.url) return;

      try {
        setIsLoadingVCF(true);
        setVcfError(null);
        const parsedContact = await parseVCFFromUrl(
          fileFromPresignedURLData.url,
        );
        setContact(parsedContact);
      } catch (err) {
        setVcfError(
          err instanceof Error ? err.message : "Failed to parse VCF file",
        );
      } finally {
        setIsLoadingVCF(false);
      }
    };

    void parseVCF();
  }, [isVCardFile, fileFromPresignedURLData?.url]);

  const handleOpenFolderSelect = () => {
    if (!matter?.data) return;
    setIsFolderSelectOpen(true);
  };

  const handleFolderSelected = async (folderId: string) => {
    if (!matterId) return;

    setIsPreparingFile(true);
    try {
      const downloadedFile = await downloadMessageMediaFile(s3Key);

      openFileModal({
        matterId,
        editing: false,
        currentFolderId: folderId,
        isFolder: false,
        initialFile: downloadedFile,
        afterCreateFile: () => {
          setIsAdded(true);
        },
      });
    } catch (error) {
      console.error("Failed to prepare message media for the file repo", error);
      toast.error("Unable to prepare this file. Please try again.");
    } finally {
      setIsPreparingFile(false);
    }
  };

  const fileRepoControls = matterId ? (
    <>
      <TooltipWrapper
        message={isAdded ? "Added to file repo" : "Add to file repo"}
      >
        <Button
          type="button"
          aria-label={isAdded ? "Added to file repo" : "Add to file repo"}
          size="sm"
          variant="outline"
          onClick={handleOpenFolderSelect}
          disabled={isAdded || isPreparingFile || !matter?.data}
          className="h-8 w-8 rounded-full p-0 shadow-lg"
        >
          {isPreparingFile ? (
            <LoadingSpinner className="h-4 w-4" />
          ) : isAdded ? (
            <FileCheck className="h-4 w-4" />
          ) : (
            <FilePlus className="h-4 w-4" />
          )}
        </Button>
      </TooltipWrapper>
      {matter?.data && (
        <SelectDestinationFolderSlideover
          open={isFolderSelectOpen}
          setOpen={setIsFolderSelectOpen}
          matter={matter.data}
          onFolderSelected={(folder) => {
            void handleFolderSelected(folder?.id ?? "");
          }}
          title="Save file to file repo"
          description="Choose where this file should be saved."
          overlayClassName="z-[108] bg-black/50 backdrop-blur-md"
          className="z-[109]"
        />
      )}
    </>
  ) : null;

  // Render business card component
  if (isVCardFile) {
    const displayName =
      contact?.fullName ||
      [contact?.firstName, contact?.lastName].filter(Boolean).join(" ") ||
      "Business card";

    // Check if we have any contact information to display
    const hasContactInfo =
      contact &&
      (contact.organization ||
        (contact.phone && contact.phone.length > 0) ||
        (contact.email && contact.email.length > 0) ||
        (contact.address && contact.address.length > 0));

    return (
      <div
        className={cn(
          "flex flex-col items-start gap-0.5",
          direction === "OUTBOUND" && "items-end",
        )}
      >
        <div className="relative flex items-center gap-2">
          <div className="bg-muted/30 flex h-32 w-64 items-center justify-center rounded-md border">
            {isLoadingVCF ? (
              <div className="flex flex-col items-center gap-2 text-center">
                <LoadingSpinner className="h-6 w-6" />
                <Typography className="text-muted-foreground text-xs">
                  Loading contact...
                </Typography>
              </div>
            ) : vcfError ? (
              <div className="flex flex-col items-center gap-2 text-center">
                <IdCard className="text-muted-foreground h-8 w-8" />
                <Typography className="text-sm font-medium">
                  Business card
                </Typography>
                <Typography className="text-destructive text-xs">
                  Failed to parse
                </Typography>
              </div>
            ) : contact && hasContactInfo ? (
              <div className="flex h-full w-full flex-col p-3">
                <div className="mb-2 flex items-center gap-2">
                  <IdCard className="text-primary h-4 w-4" />
                  <Typography className="truncate text-sm font-medium">
                    {displayName}
                  </Typography>
                </div>

                {contact.organization && (
                  <Typography className="text-muted-foreground mb-1 text-xs">
                    {contact.organization}
                  </Typography>
                )}

                {contact.phone && contact.phone.length > 0 && (
                  <div className="mb-1 flex items-center gap-1">
                    <Phone className="text-muted-foreground h-3 w-3" />
                    <Typography className="text-muted-foreground truncate text-xs">
                      {formatLocalOrIntlPhoneNumber({
                        number: contact.phone[0],
                      }) || contact.phone[0]}
                    </Typography>
                  </div>
                )}

                {contact.email && contact.email.length > 0 && (
                  <div className="mb-1 flex items-center gap-1">
                    <Mail className="text-muted-foreground h-3 w-3" />
                    <Typography className="text-muted-foreground truncate text-xs">
                      {contact.email[0]}
                    </Typography>
                  </div>
                )}

                {contact.address &&
                  contact.address.length > 0 &&
                  contact.address[0] && (
                    <div className="flex items-center gap-1">
                      <MapPin className="text-muted-foreground h-3 min-w-3" />
                      <Typography className="text-muted-foreground truncate text-xs">
                        {contact.address[0]}
                      </Typography>
                    </div>
                  )}
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2 text-center">
                <IdCard className="text-muted-foreground h-8 w-8" />
                <Typography className="text-sm font-medium">
                  Business card
                </Typography>
              </div>
            )}
          </div>

          {fileRepoControls}
        </div>
      </div>
    );
  }

  if (preview.isError) {
    return (
      <div className="flex flex-col items-start gap-2 rounded-md border p-4">
        <p role="alert" className="text-sm">
          Unable to load this attachment.
        </p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={preview.isFetching}
          onClick={() => void preview.refetch()}
        >
          Try again
        </Button>
      </div>
    );
  }

  if (fileFromPresignedURLData?.contentType.startsWith("video/")) {
    return (
      <div
        className={cn(
          "flex items-center gap-2",
          direction === "OUTBOUND" && "self-end",
        )}
      >
        <CommunicationVideo
          s3Key={s3Key}
          url={fileFromPresignedURLData.url}
          onRetry={async () => (await preview.refetch()).isSuccess}
          isRefreshing={preview.isFetching}
        />
        {fileRepoControls}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex flex-col items-start gap-0.5",
        direction === "OUTBOUND" && "items-end",
      )}
    >
      <div className="relative flex items-center gap-2">
        <div className="relative h-64 w-64 overflow-hidden rounded-md bg-gray-100">
          {fileFromPresignedURLData?.url ? (
            <button
              ref={previewTriggerRef}
              type="button"
              aria-label="Enlarge image"
              className="group focus-visible:ring-ring relative h-full w-full cursor-zoom-in focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset"
              onClick={() => {
                setIsPreviewOpen(true);
              }}
            >
              <img
                src={fileFromPresignedURLData.url}
                alt="Text message attachment"
                className="h-full w-full object-cover"
                crossOrigin="anonymous"
              />
              <span className="absolute right-2 bottom-2 rounded-full bg-black/60 p-2 text-white opacity-80 group-hover:opacity-100 group-focus-visible:opacity-100">
                <ZoomIn className="h-4 w-4" />
              </span>
            </button>
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <LoadingSpinner />
            </div>
          )}
        </div>

        {fileRepoControls}
      </div>
      {isPreviewOpen && (
        <CommunicationImagePreview
          s3Key={s3Key}
          onClose={() => {
            setIsPreviewOpen(false);
          }}
          onCloseAutoFocus={() => previewTriggerRef.current?.focus()}
        />
      )}
    </div>
  );
};
